import { Component, computed, effect, input, OnDestroy, output, signal, viewChild } from '@angular/core';
import { RidAssessmentToast } from '../../components/rid-assessment-toast/rid-assessment-toast';
import { RidBottomButton } from '../../components/rid-bottom-button/rid-bottom-button';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidSegmentedWordCard } from '../../components/rid-segmented-word-card/rid-segmented-word-card';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidWord } from '../../components/rid-word/rid-word';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { AudioEffectPlayer } from '../../utils/audio.utils';
import { WordToKlankConverter } from '../../utils/klank.utils';

@Component({
  selector: 'rid-dictation-visual',
  imports: [
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidBottomButton,
    RidAssessmentToast,
    RidSegmentedWordCard,
    RidOverlayFeedback,
    RidWord,
    RidSfxDirective,
  ],
  templateUrl: './rid-dictation-visual.html',
  host: {
    class: 'block',
  },
})
export class RidDictationVisual implements OnDestroy {
  private readonly previewInstructionLeadMs = 1000;
  private previewTimeout: ReturnType<typeof setTimeout> | undefined;

  words = input.required<{ word: string; audio: string }[]>();
  previewMs = input<number>(1500);

  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  segmentedWordCard = viewChild<RidSegmentedWordCard>('segmentedWordCard');
  index = signal(0);
  word = computed(() => this.words()[this.index()]);
  state = signal<'PREVIEW' | 'WRITING' | 'FEEDBACK'>('PREVIEW');

  segmentsMap = computed(() => {
    const map = new Map<number, string[]>();
    this.words().forEach((wordObj, index) => {
      map.set(index, WordToKlankConverter.breakupWord(wordObj.word));
    });
    return map;
  });

  finished = output<void>();

  constructor() {
    effect(() => {
      if (this.state() !== 'PREVIEW') {
        this.clearPreviewTimeout();
        return;
      }

      this.clearPreviewTimeout();
      this.previewTimeout = setTimeout(() => {
        this.previewTimeout = undefined;
        if (this.state() === 'PREVIEW') {
          this.state.set('WRITING');
        }
      }, this.previewInstructionLeadMs + this.previewMs());
    });
  }

  ngOnDestroy() {
    this.clearPreviewTimeout();
  }

  next() {
    const isLastIndex = this.index() === this.words().length - 1;

    if (isLastIndex) {
      this.finished.emit();
    } else {
      this.index.set(this.index() + 1);
      this.state.set('PREVIEW');
    }
  }

  async markCorrect() {
    AudioEffectPlayer.play('CORRECT');
    this.overlayFeedback().showCorrect();

    this.next();
  }

  async markIncorrect() {
    AudioEffectPlayer.play('INCORRECT');
    this.overlayFeedback().showIncorrect();

    this.next();
  }

  private clearPreviewTimeout() {
    if (this.previewTimeout === undefined) return;

    clearTimeout(this.previewTimeout);
    this.previewTimeout = undefined;
  }
}
